import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useGetCardBuyerTransactionsQuery } from 'services/api/transactions';
import { setPage } from 'services/transactions/transactionsSlice';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import { useInfiniteScroll } from 'pages/Admin/AuctionDetailNew/hooks/useInfiniteScroll';
import TransactionRecordCard from '../components/TransactionRecordCard';
import MobileTransactionCard from '../components/MobileTransactionCard';
import CompactTransactionCard from '../components/CompactTransactionCard';

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #666;
  font-size: 16px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const toApiFilters = (filters: Record<string, any>) => {
  const result: Record<string, any> = {};
  if (filters.keyword) result.keyword = filters.keyword;
  if (filters.vin) result.vin = filters.vin;
  if (filters.seller_ids?.length) result.seller_ids = filters.seller_ids;
  if (filters.status?.length) result.status = filters.status;
  return result;
};

interface TransactionsTabProps {
  viewMode?: 'full' | 'compact';
}

const TransactionsTab = ({ viewMode = 'full' }: TransactionsTabProps) => {
  const { page, perPage, filters } = useSelector((state: any) => state.transactions.card);
  const { data, isFetching } = useGetCardBuyerTransactionsQuery({ limit: perPage, offset: page, ...toApiFilters(filters) });
  const dispatch = useDispatch();
  const { isMobile } = useDeviceInfo();

  const transactions = useMemo(() => data?.rows?.ids?.map((id: any) => data?.rows?.entities?.[id]) ?? [], [data]);
  const transactionsCount = useMemo(() => data?.count ?? 0, [data]);

  const loadMore = () => {
    if (perPage * page >= transactionsCount) return;
    dispatch(setPage({ page: page + 1, tab: 'card' }));
  };

  const canLoadMore = perPage * page < transactionsCount;

  const { sentinelRef } = useInfiniteScroll({
    loading: isFetching,
    hasMore: canLoadMore,
    onLoadMore: loadMore,
  });

  if (!isFetching && transactions.length === 0) {
    return <EmptyState>No card transactions found.</EmptyState>;
  }

  const CardComponent = isMobile ? MobileTransactionCard : viewMode === 'compact' ? CompactTransactionCard : TransactionRecordCard;

  return (
    <>
      <ListWrapper>
        {transactions.map((vehicle: any) => (
          <CardComponent
            key={vehicle.id}
            vehicle={vehicle}
            queryEndpoint="getCardBuyerTransactions"
            reduxTab="card"
          />
        ))}
      </ListWrapper>
      <LoaderWrapper>
        {isFetching && <CircularProgress size={32} />}
        <Sentinel ref={sentinelRef} />
      </LoaderWrapper>
    </>
  );
};

export default TransactionsTab;
